/**
 * MillOS marketing-site design tokens.
 *
 * The public marketing site (landing, pricing, compare, compliance/accounting
 * centres, resources hub, blog/guides/calculators, legal pages) uses its own
 * brand system — separate from the authenticated app's DESIGN.md cream/coral
 * theme defined in `theme.ts`. That's a deliberate product decision, not an
 * oversight: the two are different visual systems for different audiences
 * (public marketing vs. the logged-in product), so this file is intentionally
 * never imported by `theme.ts` or vice versa.
 *
 * This mirrors `theme.ts`'s own documented "plain constants" pattern (see the
 * comment above its `CATEGORY_COLORS`/`UTILITY_COLORS`) rather than its
 * MUI-`createTheme()` pattern: the marketing site doesn't need a second MUI
 * `ThemeProvider` or dynamic theming, so a hook-based `useTheme()` indirection
 * would be ceremony with no payoff. Every marketing page/component imports
 * directly from here (`import { marketingColors as C, marketingFonts as F }`)
 * so a brand tweak happens in one place instead of being hunted down across
 * dozens of scattered literal hex codes.
 */

export const marketingColors = {
  // --- Core brand ---
  brightBlue: '#5AA9FF',
  blue: '#2563EB',
  deepBlue: '#1B46C4',
  navy: '#0F1F3D',
  mist: '#F4F7FC',
  gradient: 'linear-gradient(95deg, #5AA9FF, #1B46C4)',
  // Full-bleed section backdrop (hero, pillar-page hero, closing CTAs) — a
  // gradient built from our own brand colors rather than the Catalis
  // template's licensed stock photography, so those sections have no
  // dependency on a third party's hosted image at all.
  heroGradient: 'linear-gradient(160deg, #0F1F3D 0%, #1B46C4 55%, #5AA9FF 100%)',

  // --- Legacy Catalis-template accents still used across most section chrome ---
  accent: '#0054f9',
  ink: '#131313',
  inkSoft: '#1d1d1d',
  body: '#4c4c4c',
  bodySoft: '#8a94a6',
  hairline: '#efeff2',
  cardHairline: '#eceef4',
  panel: '#f5f5f5',
  panelSoft: '#f6f8fc',
  white: '#ffffff',
  success: '#1f9d57',
  successBg: 'rgba(31,157,87,0.12)',
  warning: '#c47a12',
  warningBg: 'rgba(196,122,18,0.12)',
  danger: '#d64545',
  border: '#e6e9f0',

  // --- Additional named tints (previously scattered raw hex literals) ---
  muted: '#8a8a8a', // secondary metadata text (dates, byline separators)
  proseText: '#2a2a2a', // article/guide body paragraph text
  tintBlue: '#eef2ff', // the standard light-blue chip/callout/hover background
  tintBlueAlt: '#eef3ff', // a near-identical one-off shade used on Home's invoice-capture mock chips — kept distinct rather than merged so no pixel changes
  tintViolet: '#f7f5ff', // light violet, paired with tintBlue in glass-panel gradients
  tintSuccess: '#eafaf1', // light green success-state background
  tintWarning: '#fff4e5', // light orange warning-state background
  tintDivider: '#eef0f5', // faint divider line inside dark result panels
  chartBarLight: '#c9d6f2', // unfilled/past bar segments in mini bar charts
  glassBorder: '#e6e9f5', // border on the frosted-glass feature panels
  highlightBg: '#eef2ff', // (alias of tintBlue) highlighted row background
  highlightBorder: '#d7e0fb', // border for a highlighted row (e.g. "just added" flat row)
  orbitDashInner: '#c7d0ea', // inner dashed ring, integrations orbit graphic
  orbitDashOuter: '#d6ddf0', // outer dashed ring, integrations orbit graphic
  iconOnDark: '#9cc0ff', // icon stroke color on the dark "Core features" cards
  accentOnDark: '#6fa8ff', // accent stroke/sparkle color on dark navy backgrounds
  avatarBg: '#dfe6f5', // circular initials-avatar background
  avatarText: '#4c5670', // circular initials-avatar text color
  connectorLine: '#dfe3ec', // vertical connector line between timeline steps
  mutedDot: '#e0e3ea', // "not yet reached" step dot in reminder cascades
  crossIcon: '#c9ccd6', // the "no" cross-mark stroke in comparison tables
  partialLabel: '#b45309', // the "Partial" text label in comparison tables
  violetAccent: '#7a5cff', // violet accent bar (e.g. "capital allowance" progress bar)
  tocNumber: '#5b8cff', // numbered list markers in a guide's dark "In this guide" box
  zebraStripe: '#fafafb', // alternate-row background in the comparison table
  uploadBoxBg: '#f9fbff', // dashed drop-zone background in mock upload panels

  // Article-body callout tones (info reuses tintBlue/accent above)
  calloutWarnBg: '#fff7ed',
  calloutWarnBorder: '#fed7aa',
  calloutSuccessBg: '#ecfdf5',
  calloutSuccessBorder: '#a7f3d0',

  // The "Core features" section's dark gradient card background
  coreFeaturesGradient: 'linear-gradient(160deg,#0a1230 0%,#0b1b4d 45%,#08122e 100%)',
  navyDeepest: '#0a1230', // matches the gradient's own darkest stop, reused for button text on a white CTA

  // Per-document-format badge colors (Templates page) — our own design
  // choice for 3 file types, unlike the brand colors below which are fixed
  // by someone else's identity guidelines.
  formatBadge: {
    PDF: '#0054f9',
    XLSX: '#1f8a5b',
    DOCX: '#2a6fdb',
  },

  // --- MillOS rebrand (home, nav, footer — see CHANGELOG) ---
  // The 4 windmill-blade colors (MillosMark/icons.tsx), reused wherever the
  // same accent family shows up as text/border/glow color across the new
  // Nav, Footer and home-page components.
  millosBlue: '#1b46c4',
  millosCyan: '#2fb6e8',
  millosViolet: '#5a7bff',
  millosTeal: '#22d3b0',
  millosMint: '#54E6A0', // primary CTA background (replaces the old blue CTA on these components)
  millosWarnBrown: '#96590A',
  millosDanger: '#B93030',
  millosDeepNavy: '#0a1230',
} as const;

/**
 * Deliberately NOT included above, and left as raw literals at their call
 * sites: (1) each UK bank's own real brand color in the Accounting Centre's
 * bank-logo grid (`routes/AccountingCentrePage.tsx`'s `banks` array) — those
 * are per-row data describing 12 different external companies' fixed
 * identities, not a MillOS design decision; (2) Google's and WhatsApp's
 * official multi-color logo paths in the same page's integrations graphic —
 * same reasoning, an authentic third-party brand mark isn't ours to rename.
 * Homogenizing either into this file would misleadingly imply they're part
 * of MillOS's own palette and could drift from the real brand colors they
 * intentionally match.
 */

export const marketingFonts = {
  display: "'LibreCasCond', Georgia, serif",
  ui: "'Sora', sans-serif",
  body: "'Roboto', sans-serif",
  // MillOS rebrand additions
  wordmark: "'Outfit', sans-serif",
  statLabel: "'Inter Tight', sans-serif",
  mono: "'JetBrains Mono', monospace",
} as const;

export const marketingRadius = {
  pill: 32,
  card: 24,
  cardLg: 32,
  chip: 20,
} as const;

export const marketingShadow = {
  glass: '0 20px 46px rgba(4,10,30,0.16)',
  product: '0 30px 70px rgba(0,0,0,0.08)',
  nav: '0 12px 34px rgba(0,0,0,0.06)',
  dropdown: '0 24px 60px rgba(0,0,0,0.12)',
} as const;

export const glassCard = {
  backdropFilter: 'blur(16px)',
  background: 'rgba(255,255,255,0.62)',
  border: '1px solid rgba(255,255,255,0.7)',
} as const;

/**
 * Shared layout measurements for marketing copy blocks — pulled out as named
 * constants (rather than one-off inline values) the same way colors/fonts
 * are, so a hero/section-copy width or spacing rule can be tuned in one
 * place instead of hunting down a raw `maxWidth`/`marginTop` in each page.
 */
export const marketingLayout = {
  // Body copy reads best at roughly 65-70 characters per line; ~38rem (608px)
  // is the standard "readable measure" width for an 18px paragraph.
  heroCopyMaxWidth: '38rem',
  // Gap between the hero headline and the paragraph copy beneath it — kept
  // tight (even negative, to counteract the whitespace the headline's own
  // 1.1 line-height leaves below its text) so the section reads as one
  // unified block, not two floating pieces of text.
  heroHeadlineToCopyGap: '-6px',
  // Gap between paragraphs within the hero copy itself.
  heroCopyParagraphGap: '12px',
  // Extra gap before the hero's final punchy closing line, so it reads as a
  // deliberate standalone takeaway rather than a leftover sentence tacked
  // onto the paragraph above it.
  heroClosingLineGap: '22px',
} as const;


export const gradientText = {
  background: marketingColors.gradient,
  WebkitBackgroundClip: 'text' as const,
  backgroundClip: 'text' as const,
  color: 'transparent' as const,
};

// Public asset paths (frontend/public/marketing/*), referenced by absolute path
export const marketingAssets = {
  lockup: '/marketing/landvale-lockup.svg',
  arrow: '/marketing/landvale-arrow.svg',
  appIcon: '/marketing/landvale-app-icon.svg',
  // Hero (top) and closing-CTA (bottom) section backdrops — self-hosted, own photography
  // (not the Catalis template's licensed stock imagery this replaced). Always used together
  // with marketingColors.heroGradient as a translucent overlay (see LandingPage.tsx) so the
  // white text/eyebrow/CTA button on top stay readable regardless of the photo's own contrast.
  heroCloudBg: '/marketing/hero-cloud-bg.avif',
  ctaLandBg: '/marketing/cta-land-bg.avif',
} as const;
